import React, { useCallback, useContext } from 'react';

import { Alert } from '@mui/material';
import classNames from 'classnames';
import { format } from 'date-fns';
import { nanoid } from 'nanoid';
import numeral from 'numeral';
import { MapDataContext } from '../context/map-data';
import { OrderDetails, OrderItem, PaymentType } from '../core/global';
import { ModelShapeItem } from '../data/layers';
import tableStyles from "../styles/components/table.module.scss";
import { CreateSummary } from './form-components';
import { StyledMarkdown } from './typeface';

const getCost = (cost: number, tokens: number, paymentType: PaymentType) => {
  if (!cost && !tokens && !paymentType) {
    return `N/A`
  }
  if (paymentType === "card" || paymentType === "po") {
    return `£${numeral(cost).format("0,0.00")}`
  }
  if (paymentType === "tokens") {
    return `${numeral(tokens).format("0,0")} tokens`
  }
  return `Free`
}

export interface TableProps {
  data: OrderItem[] | null
  orderDetails: OrderDetails | null
}

export const OrderItemsSummaryTable = ({
  data,
  orderDetails,
}: TableProps ) => {

  const {
    dataSources,
  } = useContext(MapDataContext);

  // const {
  //   paymentType,
  //   createdAt,
  //   transactionId
  // } = useMemo(() => {
  //   if (orderDetails) {
  //     const { type, created_at, transaction_id } = orderDetails
  //     return { 
  //       paymentType: type,
  //       createdAt: created_at,
  //       transactionId: transaction_id
  //      }
  //   }
  //   return { paymentType: null }
  // }, [orderDetails])

  if (!data || data.length === 0) {
    return null
  }

  return (
    <>
      <table className={tableStyles.table}>
        <thead>
          <tr>
            <th className={tableStyles.fieldName} style={{minWidth: '300px'}}>
              Location/Model
            </th>
            <th>Date range</th>
            <th className={tableStyles.currency}>
              <span>Cost/Tokens</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map(item => {
              if (item) {
                const {
                  id,
                  lat,
                  lon,
                  model,
                  provider,
                  geography,
                  version,
                  cost,
                  tokens,
                  start_date,
                  end_date,
                  project_id,
                  type,
                  params,
                } = item
                
                const modelKey = `${provider}-${model}-${geography}-${version}`

                const currentDataSource: ModelShapeItem | null = (dataSources && Object.prototype.hasOwnProperty.call(dataSources, modelKey)) ? dataSources[modelKey] : null
                const config = currentDataSource?.options || null

                const range = `${format(new Date(start_date), 'MM/yyyy')} - ${format(new Date(end_date), 'MM/yyyy')}`
                const isCompanyPurchase = !!(typeof params !== "undefined" && params !== null && params.created_at)
                return (
                  <React.Fragment key={id}>
                    <tr key={id} className={classNames({
                      [tableStyles.noBorder]: isCompanyPurchase,
                    })}>
                      <td>
                        <strong>{lat}, {lon}</strong><br />
                        {(dataSources && Object.prototype.hasOwnProperty.call(dataSources, modelKey))? dataSources[modelKey].name : model}
                      </td>
                      <td className={tableStyles.date}>{range}</td>
                      <td className={tableStyles.currency}>{getCost(cost, tokens, type)}</td>
                    </tr>
                    {(config && params && params.config) &&  <tr>
                      <td className={tableStyles.date} colSpan={3}>
                        <CreateSummary
                          className={tableStyles.summary}
                          compact
                          values={params.config}
                          shape={config}
                        />
                      </td>
                    </tr>
                    }
                    {(isCompanyPurchase === true && params) ? 
                      <tr>
                        <td className={tableStyles.date} colSpan={3}>
                        <Alert severity="info">
                          <StyledMarkdown>
                             {`This is purchase request by **${params.first_name || 'Unknown'} ${params.last_name || 'User'}**, requested on ${format(new Date(params.created_at), 'dd/MM/yyyy')}. `}
                          </StyledMarkdown>
                        </Alert>
                        </td>
                      </tr>
                    :
                      null
                    }
                  </React.Fragment>
                )  
              }
              return null
            })
          }
        </tbody>
        <tfoot>
          <OrderCostSummaryTable
            columnsPadding={1} 
            orderDetails={orderDetails} 
          />
        </tfoot>
      </table>
    </>
  )
}


interface OrderCostSummaryTableProps {
  fullTable?: boolean
  columnsPadding?: number
  orderDetails: OrderDetails | null
}

export const OrderCostSummaryTable = ({
  fullTable,
  columnsPadding = 0,
  orderDetails,
}: OrderCostSummaryTableProps ) => {


  const content = useCallback( () => {
    if (!orderDetails) {
      return null
    }

    const columns = [...new Array(columnsPadding)]
    
    const { 
      type: paymentType, 
      vat_exempt: vatExempt,
      total,
      vat_value: vatValue,
      discount_percent: discountPercentage, 
      discount_tokens: discountTokens,
      vat_percent: vatPercentage,
      tokens: totalTokens,
      discount: discountValue,
      subtotal,
      } = orderDetails


    const subtotalTokens = 0

    if (paymentType === "subscription") {
      return (
        <>
          <tr>
            {columns.map((c, index) => <td key={nanoid()}></td>)}
            <th colSpan={2}>This is a free data acquisition included in your subscription.</th>
            {!fullTable && <td></td>}
          </tr>
        </>
      )
    }

    return (
      <>
        {(subtotal > 0 || subtotalTokens > 0) &&
          <tr>
            {columns.map((c, index) => <td key={nanoid()}></td>)}
            <th>Subtotal:</th>
            <td className={tableStyles.currency}>{getCost(subtotal, subtotalTokens, paymentType)}</td>
            {!fullTable && <td></td>}
          </tr>
        }
        {discountPercentage > 0 && 
          <tr>
            {columns.map(c => <td key={nanoid()}></td>)}
            <th>Discount @{discountPercentage}%:</th>
            <td className={tableStyles.currency}>-{getCost(discountValue, discountTokens, paymentType)}</td>
            {!fullTable && <td></td>}
          </tr>
        }
        {paymentType === "tokens" 
          && discountPercentage > 0 && 
          <tr>
            {columns.map(c => <td key={nanoid()}></td>)}
            <th colSpan={2}>Discount @{discountPercentage}% applied at tokens purchase</th>
            {!fullTable && <td></td>}
          </tr>
        }
        {paymentType === "tokens" 
          && discountPercentage === 0 && 
          <tr>
            {columns.map(c => <td key={nanoid()}></td>)}
            <th colSpan={2}>Discount applied at tokens purchase</th>
            {!fullTable && <td></td>}
          </tr>
        }
        {(paymentType === 'card' || paymentType === 'po') && !vatExempt && 
          <tr>
            {columns.map(c => <td key={nanoid()}></td>)}
            <th>V.A.T. @{vatPercentage}%:</th>
            <td className={tableStyles.currency}>{getCost(vatValue, 0, paymentType)}</td>
            {!fullTable && <td></td>}
          </tr>
        }
        <tr>
          {columns.map(c => <td key={nanoid()}></td>)}
          <th>Total:</th>
          <td className={tableStyles.currency}>{getCost(total, totalTokens, paymentType)}</td>
          {!fullTable && <td></td>}
        </tr>
      </>
    )
  }, [columnsPadding, fullTable])

  if (fullTable) {
    return (
      <>
        <table className={classNames(tableStyles.table)}>
          <tbody>
            {content()}
          </tbody>
        </table>
      </>
    )
  }

  return (
    <>
      {content()}
    </>
  )
}
